import React, { useEffect, useState } from "react";
import { Plus, Trash2, Truck, Gift, Award, Handshake, Download, FileSpreadsheet, Edit2, DollarSign, Trophy as TrophyIcon } from "lucide-react";
import { toast } from "sonner";
import api, { BACKEND_URL } from "@/lib/api";

export default function VendorsTab({ event }) {
  const [section, setSection] = useState("vendors");
  return (
    <div>
      <div className="flex gap-2 mb-5 flex-wrap">
        {[
          { key: "vendors", label: "Vendors", Icon: Truck },
          { key: "collabs", label: "Collaborations", Icon: Handshake },
          { key: "trophies", label: "Trophies & Cash Prizes", Icon: Award },
          { key: "merch", label: "Merchandise & Goodies", Icon: Gift },
        ].map((s) => (
          <button key={s.key} onClick={() => setSection(s.key)} className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm font-semibold ${section === s.key ? "brand-gradient text-white" : "bg-white text-slate-600 border border-slate-200"}`} data-testid={`vendor-section-${s.key}`}>
            <s.Icon className="w-4 h-4" /> {s.label}
          </button>
        ))}
      </div>
      {section === "vendors" && <Vendors event={event} />}
      {section === "collabs" && <Collabs event={event} />}
      {section === "trophies" && <Trophies event={event} />}
      {section === "merch" && <Merch event={event} />}
    </div>
  );
}

// ───────────────── VENDORS ─────────────────
function Vendors({ event }) {
  const [vendors, setVendors] = useState([]);
  const [eventVendors, setEventVendors] = useState([]);
  const [form, setForm] = useState({ vendor_id: "", category: "", notes: "", products: [] });
  const [productDraft, setProductDraft] = useState({ label: "", quantity: "", unit_price: "" });
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({ products: [], notes: "" });

  const load = async () => {
    const [v, ev] = await Promise.all([api.get("/vendors"), api.get(`/events/${event.event_id}/vendors`)]);
    setVendors(v.data); setEventVendors(ev.data);
  };
  useEffect(() => { load(); }, []);

  const addProductToForm = () => {
    if (!productDraft.label.trim()) return toast.error("Product label required");
    setForm({
      ...form,
      products: [...form.products, {
        label: productDraft.label.trim(),
        quantity: parseFloat(productDraft.quantity) || 0,
        unit_price: parseFloat(productDraft.unit_price) || 0,
      }],
    });
    setProductDraft({ label: "", quantity: "", unit_price: "" });
  };

  const removeProductFromForm = (idx) => {
    setForm({ ...form, products: form.products.filter((_, i) => i !== idx) });
  };

  const assign = async (e) => {
    e.preventDefault();
    if (!form.vendor_id) return toast.error("Pick a vendor");
    const cost = form.products.reduce((s, p) => s + (p.quantity * p.unit_price), 0);
    try {
      await api.post(`/events/${event.event_id}/vendors`, { ...form, cost });
      toast.success("Vendor allotted");
      setForm({ vendor_id: "", category: "", notes: "", products: [] });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this vendor allotment?")) return;
    await api.delete(`/event-vendors/${id}`);
    load();
  };

  const startEdit = (ev) => {
    setEditingId(ev.id);
    setEditDraft({ products: [...(ev.products || [])], notes: ev.notes || "" });
  };

  const saveEdit = async (ev) => {
    const cost = editDraft.products.reduce((s, p) => s + ((parseFloat(p.quantity) || 0) * (parseFloat(p.unit_price) || 0)), 0);
    try {
      await api.patch(`/event-vendors/${ev.id}`, { products: editDraft.products, notes: editDraft.notes, cost });
      toast.success("Updated");
      setEditingId(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const downloadReqs = (ev) => {
    const token = localStorage.getItem("munners_token");
    window.open(`${BACKEND_URL}/api/event-vendors/${ev.id}/requirements.xlsx?t=${token}`, "_blank");
  };

  const formTotal = form.products.reduce((s, p) => s + (p.quantity * p.unit_price), 0);

  return (
    <div className="grid lg:grid-cols-3 gap-5">
      <form onSubmit={assign} className="dashboard-card p-5 space-y-3 h-fit">
        <h4 className="font-bold">Allot Vendor</h4>
        <select className="input-pill" value={form.vendor_id} onChange={(e) => {
          const v = vendors.find((x) => x.vendor_id === e.target.value);
          setForm({ ...form, vendor_id: e.target.value, category: v?.category || form.category });
        }} data-testid="vendor-select">
          <option value="">Select a vendor from directory</option>
          {vendors.map((v) => <option key={v.vendor_id} value={v.vendor_id}>{v.name} · {v.category}</option>)}
        </select>
        <input placeholder="Category (auto-filled)" className="input-pill" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />

        <div className="pt-2 border-t border-slate-100">
          <div className="text-xs font-bold text-slate-600 mb-1.5">Add products / line-items</div>
          <div className="grid grid-cols-12 gap-1.5">
            <input placeholder="Product / item label" className="input-pill py-2 text-xs col-span-12" value={productDraft.label} onChange={(e) => setProductDraft({ ...productDraft, label: e.target.value })} data-testid="vendor-product-label" />
            <input type="number" placeholder="Quantity" className="input-pill py-2 text-xs col-span-5" value={productDraft.quantity} onChange={(e) => setProductDraft({ ...productDraft, quantity: e.target.value })} data-testid="vendor-product-qty" />
            <input type="number" placeholder="Per-item price ₹" className="input-pill py-2 text-xs col-span-5" value={productDraft.unit_price} onChange={(e) => setProductDraft({ ...productDraft, unit_price: e.target.value })} data-testid="vendor-product-price" />
            <button type="button" onClick={addProductToForm} className="btn-primary col-span-2 py-2 px-0" data-testid="vendor-product-add"><Plus className="w-3 h-3" /></button>
          </div>
        </div>

        {form.products.length > 0 && (
          <div className="mt-2 space-y-1">
            {form.products.map((p, i) => (
              <div key={i} className="flex items-center justify-between text-xs bg-orange-50 p-2 rounded-lg">
                <span className="font-medium text-slate-800 truncate">{p.label} × {p.quantity} · ₹{p.unit_price}</span>
                <span className="flex items-center gap-2">
                  <span className="text-orange-700 font-bold">₹{(p.quantity * p.unit_price).toLocaleString()}</span>
                  <button type="button" onClick={() => removeProductFromForm(i)} className="text-rose-500"><Trash2 className="w-3 h-3" /></button>
                </span>
              </div>
            ))}
            <div className="text-right text-sm font-bold text-orange-700 pt-1 border-t border-orange-100">Total: ₹{formTotal.toLocaleString()}</div>
          </div>
        )}

        <textarea rows={2} placeholder="Notes (delivery date, contact person, special instructions...)" className="input-pill" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        <button type="submit" className="btn-primary w-full py-2.5" data-testid="vendor-assign-submit">Allot vendor</button>
        {vendors.length === 0 && <p className="text-xs text-amber-600">No vendors in directory. Admin / Regional PM must add them from <b>Vendors</b> in the sidebar first.</p>}
      </form>

      <div className="lg:col-span-2 dashboard-card p-5">
        <h4 className="font-bold mb-3">Allotted Vendors</h4>
        <div className="space-y-2">
          {eventVendors.length === 0 && <div className="text-sm text-slate-400">No vendors allotted yet.</div>}
          {eventVendors.map((ev) => {
            const v = vendors.find((x) => x.vendor_id === ev.vendor_id);
            const products = ev.products || [];
            const total = products.reduce((s, p) => s + ((p.quantity || 0) * (p.unit_price || 0)), 0) || ev.cost || 0;
            const isEditing = editingId === ev.id;
            return (
              <div key={ev.id} className="p-4 rounded-xl border border-slate-100" data-testid={`event-vendor-${ev.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-bold text-slate-900">{v?.name || ev.vendor_id}</div>
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-orange-50 text-orange-700">{ev.category}</span>
                      <span className="text-sm text-orange-700 font-bold">₹{total.toLocaleString()}</span>
                    </div>
                    {ev.notes && !isEditing && <div className="text-xs text-slate-500 mt-1 italic">{ev.notes}</div>}
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => downloadReqs(ev)} className="p-1.5 rounded hover:bg-orange-50 text-orange-600" title="Download requirements (Excel)" data-testid={`download-vendor-reqs-${ev.id}`}><FileSpreadsheet className="w-4 h-4" /></button>
                    <button onClick={() => startEdit(ev)} className="p-1.5 rounded hover:bg-slate-100 text-slate-500" title="Edit"><Edit2 className="w-4 h-4" /></button>
                    <button onClick={() => remove(ev.id)} className="p-1.5 rounded hover:bg-rose-50 text-rose-500" title="Remove"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>

                {!isEditing && products.length > 0 && (
                  <div className="mt-3 border-t border-slate-100 pt-2 space-y-1">
                    {products.map((p, i) => (
                      <div key={i} className="flex items-center justify-between text-xs text-slate-600">
                        <span className="font-medium">{p.label}</span>
                        <span>{p.quantity} × ₹{(p.unit_price || 0).toLocaleString()} = <b className="text-orange-700">₹{((p.quantity || 0) * (p.unit_price || 0)).toLocaleString()}</b></span>
                      </div>
                    ))}
                  </div>
                )}

                {isEditing && (
                  <div className="mt-3 border-t border-slate-100 pt-2 space-y-2">
                    {editDraft.products.map((p, i) => (
                      <div key={i} className="grid grid-cols-12 gap-1.5 items-center">
                        <input className="input-pill py-1.5 text-xs col-span-6" value={p.label} onChange={(e) => {
                          const arr = [...editDraft.products]; arr[i] = { ...arr[i], label: e.target.value };
                          setEditDraft({ ...editDraft, products: arr });
                        }} />
                        <input type="number" className="input-pill py-1.5 text-xs col-span-2" value={p.quantity} onChange={(e) => {
                          const arr = [...editDraft.products]; arr[i] = { ...arr[i], quantity: parseFloat(e.target.value) || 0 };
                          setEditDraft({ ...editDraft, products: arr });
                        }} />
                        <input type="number" className="input-pill py-1.5 text-xs col-span-3" value={p.unit_price} onChange={(e) => {
                          const arr = [...editDraft.products]; arr[i] = { ...arr[i], unit_price: parseFloat(e.target.value) || 0 };
                          setEditDraft({ ...editDraft, products: arr });
                        }} />
                        <button onClick={() => setEditDraft({ ...editDraft, products: editDraft.products.filter((_, j) => j !== i) })} className="text-rose-500 col-span-1"><Trash2 className="w-3 h-3" /></button>
                      </div>
                    ))}
                    <button onClick={() => setEditDraft({ ...editDraft, products: [...editDraft.products, { label: "", quantity: 0, unit_price: 0 }] })} className="btn-ghost py-1 text-xs"><Plus className="w-3 h-3" /> Add product</button>
                    <textarea rows={2} placeholder="Notes" className="input-pill" value={editDraft.notes} onChange={(e) => setEditDraft({ ...editDraft, notes: e.target.value })} />
                    <div className="flex gap-2">
                      <button onClick={() => saveEdit(ev)} className="btn-primary py-1.5 text-xs">Save</button>
                      <button onClick={() => setEditingId(null)} className="btn-ghost py-1.5 text-xs">Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ───────────────── COLLABS ─────────────────
function Collabs({ event }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", providing: "", perks: "", we_give: "", location: "", contact_email: "", contact_phone: "" });
  const load = async () => setItems((await api.get(`/events/${event.event_id}/collaborations`)).data);
  useEffect(() => { load(); }, []);
  const add = async (e) => { e.preventDefault(); await api.post(`/events/${event.event_id}/collaborations`, form); setForm({ name: "", providing: "", perks: "", we_give: "", location: "", contact_email: "", contact_phone: "" }); load(); };
  const del = async (id) => { await api.delete(`/collaborations/${id}`); load(); };
  return (
    <div className="grid lg:grid-cols-3 gap-5">
      <form onSubmit={add} className="dashboard-card p-5 space-y-2 h-fit">
        <h4 className="font-bold">Add Collaboration</h4>
        <input required placeholder="Collaboration name" className="input-pill" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="collab-name" />
        <input placeholder="What they provide" className="input-pill" value={form.providing} onChange={(e) => setForm({ ...form, providing: e.target.value })} />
        <input placeholder="Perks / benefits" className="input-pill" value={form.perks} onChange={(e) => setForm({ ...form, perks: e.target.value })} />
        <input placeholder="What we give in return" className="input-pill" value={form.we_give} onChange={(e) => setForm({ ...form, we_give: e.target.value })} />
        <input placeholder="Location / based from" className="input-pill" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
        <input placeholder="Contact email" className="input-pill" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} />
        <input placeholder="Contact phone" className="input-pill" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} />
        <button className="btn-primary w-full py-2.5">Add</button>
      </form>
      <div className="lg:col-span-2 dashboard-card p-5">
        <h4 className="font-bold mb-3">Collaborations</h4>
        {items.length === 0 && <div className="text-sm text-slate-400">None yet.</div>}
        {items.map((c) => (
          <div key={c.collaboration_id} className="p-3 rounded-xl border border-slate-100 mb-2">
            <div className="flex justify-between"><div className="font-semibold">{c.name}</div><button onClick={() => del(c.collaboration_id)} className="text-rose-500"><Trash2 className="w-4 h-4" /></button></div>
            <div className="text-xs text-slate-500 mt-1">{c.providing} · {c.location} · {c.contact_email}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ───────────────── TROPHIES + CASH PRIZES ─────────────────
function Trophies({ event }) {
  const [items, setItems] = useState([]);
  const [committees, setCommittees] = useState([]);
  const [kind, setKind] = useState("trophy");
  const [form, setForm] = useState({ award: "", quantity: "", unit_price: "", committee_ids: [], notes: "" });
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    const [t, c] = await Promise.all([api.get(`/events/${event.event_id}/trophies`), api.get(`/events/${event.event_id}/committees`)]);
    setItems(t.data); setCommittees(c.data);
  };
  useEffect(() => { load(); }, []);

  // Committees already used for a cash-prize of the SAME name → faded/locked.
  const lockedCommitteeIds = React.useMemo(() => {
    if (kind !== "cash_prize" || !form.award.trim()) return new Set();
    const used = new Set();
    items.forEach((t) => {
      if (t.kind === "cash_prize" && (t.award || "").trim().toLowerCase() === form.award.trim().toLowerCase() && (!editingId || t.trophy_id !== editingId)) {
        (t.committee_ids || []).forEach((cid) => used.add(cid));
      }
    });
    return used;
  }, [items, kind, form.award, editingId]);

  const save = async (e) => {
    e.preventDefault();
    if (!form.award.trim()) return toast.error("Name is required");
    const payload = {
      kind,
      award: form.award.trim(),
      quantity: parseInt(form.quantity) || 0,
      unit_price: parseFloat(form.unit_price) || 0,
      committee_ids: kind === "cash_prize" ? form.committee_ids : [],
      notes: form.notes,
    };
    try {
      if (editingId) {
        await api.patch(`/trophies/${editingId}`, payload);
        toast.success("Updated");
      } else {
        await api.post(`/events/${event.event_id}/trophies`, payload);
        toast.success("Added");
      }
      setForm({ award: "", quantity: "", unit_price: "", committee_ids: [], notes: "" });
      setEditingId(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const del = async (t) => {
    if (!window.confirm(`Delete ${t.kind === "cash_prize" ? "cash prize" : "trophy"} "${t.award}"?`)) return;
    await api.delete(`/trophies/${t.trophy_id}`);
    load();
  };

  const startEdit = (t) => {
    setKind(t.kind || "trophy");
    setForm({
      award: t.award || "",
      quantity: t.quantity || "",
      unit_price: t.unit_price || t.cash_prize || "",
      committee_ids: t.committee_ids || [],
      notes: t.notes || "",
    });
    setEditingId(t.trophy_id);
  };

  const toggleCommittee = (cid) => {
    if (lockedCommitteeIds.has(cid)) return;
    setForm({
      ...form,
      committee_ids: form.committee_ids.includes(cid)
        ? form.committee_ids.filter((x) => x !== cid)
        : [...form.committee_ids, cid],
    });
  };

  const downloadXlsx = () => {
    const token = localStorage.getItem("munners_token");
    window.open(`${BACKEND_URL}/api/events/${event.event_id}/trophies.xlsx?t=${token}`, "_blank");
  };

  const formSubtotal = (parseInt(form.quantity) || 0) * (parseFloat(form.unit_price) || 0);
  const trophies = items.filter((t) => (t.kind || "trophy") === "trophy");
  const cashPrizes = items.filter((t) => t.kind === "cash_prize");

  return (
    <div className="grid lg:grid-cols-3 gap-5">
      <form onSubmit={save} className="dashboard-card p-5 space-y-3 h-fit">
        <div className="flex items-center justify-between">
          <h4 className="font-bold">{editingId ? "Edit item" : "Add item"}</h4>
        </div>
        <div className="flex gap-1 p-1 bg-slate-100 rounded-full text-xs">
          <button type="button" onClick={() => setKind("trophy")} className={`flex-1 py-1.5 rounded-full flex items-center justify-center gap-1 font-semibold ${kind === "trophy" ? "bg-white text-orange-700 shadow" : "text-slate-500"}`} data-testid="trophy-kind-trophy"><TrophyIcon className="w-3 h-3" /> Trophy</button>
          <button type="button" onClick={() => setKind("cash_prize")} className={`flex-1 py-1.5 rounded-full flex items-center justify-center gap-1 font-semibold ${kind === "cash_prize" ? "bg-white text-emerald-700 shadow" : "text-slate-500"}`} data-testid="trophy-kind-cash"><DollarSign className="w-3 h-3" /> Cash Prize</button>
        </div>

        <input required placeholder={kind === "trophy" ? "Trophy name (e.g. Best Delegate Trophy)" : "Cash prize name (e.g. Best Delegate)"} className="input-pill" value={form.award} onChange={(e) => setForm({ ...form, award: e.target.value })} data-testid="trophy-name-input" />

        {kind === "cash_prize" && (
          <div>
            <div className="text-xs text-slate-500 mb-1">Select committee(s) — faded ones are already awarded this prize name</div>
            <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto" data-testid="trophy-committees">
              {committees.length === 0 && <div className="text-xs text-amber-600">Add committees first in the Committees tab.</div>}
              {committees.map((c) => {
                const locked = lockedCommitteeIds.has(c.committee_id);
                const active = form.committee_ids.includes(c.committee_id);
                return (
                  <button
                    key={c.committee_id}
                    type="button"
                    onClick={() => toggleCommittee(c.committee_id)}
                    disabled={locked}
                    data-testid={`trophy-committee-${c.committee_id}`}
                    className={`text-xs px-2.5 py-1 rounded-full font-semibold transition ${locked ? "bg-slate-100 text-slate-400 line-through cursor-not-allowed opacity-50" : active ? "bg-emerald-600 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-emerald-300"}`}
                  >{c.name}</button>
                );
              })}
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-2">
          <input required type="number" placeholder={kind === "trophy" ? "Quantity" : "Number of prizes"} className="input-pill" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="trophy-qty" />
          <input required type="number" placeholder={kind === "trophy" ? "Per item price ₹" : "Amount per prize ₹"} className="input-pill" value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: e.target.value })} data-testid="trophy-price" />
        </div>
        {formSubtotal > 0 && (
          <div className="text-xs text-right text-slate-500">Total: <b className={kind === "trophy" ? "text-orange-700" : "text-emerald-700"}>₹{formSubtotal.toLocaleString()}</b></div>
        )}

        <textarea rows={2} placeholder={kind === "trophy" ? "Matter to be written on the trophy" : "Notes (eligibility criteria, awarding ceremony, etc.)"} className="input-pill" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="trophy-notes" />

        <button type="submit" className="btn-primary w-full py-2.5" data-testid="trophy-save"><Plus className="w-4 h-4" /> {editingId ? "Save changes" : "Add"}</button>
        {editingId && <button type="button" onClick={() => { setEditingId(null); setForm({ award: "", quantity: "", unit_price: "", committee_ids: [], notes: "" }); }} className="btn-ghost w-full py-2 text-xs">Cancel edit</button>}
      </form>

      <div className="lg:col-span-2 space-y-5">
        <div className="dashboard-card p-5">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-bold flex items-center gap-2"><TrophyIcon className="w-4 h-4 text-orange-600" /> Trophies ({trophies.length})</h4>
            <button onClick={downloadXlsx} className="btn-ghost text-xs py-1.5" data-testid="trophy-download-xlsx"><FileSpreadsheet className="w-3 h-3" /> Download all (Excel)</button>
          </div>
          {trophies.length === 0 && <div className="text-sm text-slate-400 py-4">No trophies added yet.</div>}
          <div className="space-y-2">
            {trophies.map((t) => (
              <div key={t.trophy_id} className="p-3 rounded-xl border border-slate-100" data-testid={`trophy-${t.trophy_id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-slate-900">{t.award}</div>
                    <div className="text-xs text-slate-500">{t.quantity || 0} × ₹{(t.unit_price || t.cash_prize || 0).toLocaleString()} = <b className="text-orange-700">₹{((t.quantity || 0) * (t.unit_price || t.cash_prize || 0)).toLocaleString()}</b></div>
                    {t.notes && <div className="text-xs text-slate-500 italic mt-1">&ldquo;{t.notes}&rdquo;</div>}
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => startEdit(t)} className="text-slate-500 hover:bg-slate-100 p-1 rounded"><Edit2 className="w-3.5 h-3.5" /></button>
                    <button onClick={() => del(t)} className="text-rose-500 hover:bg-rose-50 p-1 rounded"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="dashboard-card p-5">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-bold flex items-center gap-2"><DollarSign className="w-4 h-4 text-emerald-600" /> Cash Prizes ({cashPrizes.length})</h4>
          </div>
          {cashPrizes.length === 0 && <div className="text-sm text-slate-400 py-4">No cash prizes added yet.</div>}
          <div className="space-y-2">
            {cashPrizes.map((t) => (
              <div key={t.trophy_id} className="p-3 rounded-xl border border-slate-100" data-testid={`cash-prize-${t.trophy_id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-slate-900">{t.award}</div>
                    <div className="text-xs text-slate-500">₹{(t.unit_price || 0).toLocaleString()} × {t.quantity || 0} = <b className="text-emerald-700">₹{((t.quantity || 0) * (t.unit_price || 0)).toLocaleString()}</b></div>
                    {(t.committee_ids || []).length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {(t.committee_ids || []).map((cid) => (
                          <span key={cid} className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700">{committees.find((c) => c.committee_id === cid)?.name || cid}</span>
                        ))}
                      </div>
                    )}
                    {t.notes && <div className="text-xs text-slate-500 italic mt-1">&ldquo;{t.notes}&rdquo;</div>}
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => startEdit(t)} className="text-slate-500 hover:bg-slate-100 p-1 rounded"><Edit2 className="w-3.5 h-3.5" /></button>
                    <button onClick={() => del(t)} className="text-rose-500 hover:bg-rose-50 p-1 rounded"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ───────────────── MERCHANDISE ─────────────────
function Merch({ event }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", kind: "goodie", source: "", quantity: "", unit_cost: "", unit_price: "" });
  const load = async () => setItems((await api.get(`/events/${event.event_id}/merchandise`)).data);
  useEffect(() => { load(); }, []);
  const add = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      quantity: parseInt(form.quantity) || 0,
      unit_cost: parseFloat(form.unit_cost) || 0,
      unit_price: parseFloat(form.unit_price) || 0,
    };
    await api.post(`/events/${event.event_id}/merchandise`, payload);
    setForm({ name: "", kind: "goodie", source: "", quantity: "", unit_cost: "", unit_price: "" });
    load();
  };
  const del = async (id) => { await api.delete(`/merchandise/${id}`); load(); };
  return (
    <div className="grid lg:grid-cols-3 gap-5">
      <form onSubmit={add} className="dashboard-card p-5 space-y-2 h-fit">
        <h4 className="font-bold">Add Item</h4>
        <input required placeholder="Item name" className="input-pill" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="merch-name" />
        <select className="input-pill" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}><option value="goodie">Goodie</option><option value="merchandise">Merchandise</option></select>
        <input placeholder="Source / vendor" className="input-pill" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} />
        <input type="number" placeholder="Quantity" className="input-pill" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
        <input type="number" placeholder="Unit cost ₹" className="input-pill" value={form.unit_cost} onChange={(e) => setForm({ ...form, unit_cost: e.target.value })} />
        <input type="number" placeholder="Sell price ₹" className="input-pill" value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: e.target.value })} />
        <button className="btn-primary w-full py-2.5">Add</button>
      </form>
      <div className="lg:col-span-2 dashboard-card p-5">
        <h4 className="font-bold mb-3">Merchandise & Goodies</h4>
        {items.length === 0 && <div className="text-sm text-slate-400">None yet.</div>}
        {items.map((m) => (
          <div key={m.item_id} className="flex items-center justify-between p-3 rounded-xl border border-slate-100 mb-2">
            <div>
              <div className="font-semibold">{m.name} <span className="text-xs text-slate-400">({m.kind})</span></div>
              <div className="text-xs text-slate-500">Qty {m.quantity} · cost ₹{m.unit_cost} · price ₹{m.unit_price} · {m.source}</div>
            </div>
            <button onClick={() => del(m.item_id)} className="text-rose-500"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
